import { useState } from "react";
import { FILES_BASE } from "../lib/config";

export type SignedFile = { name: string; bytes: number; url: string };

/** The signed links to the files a job wrote, asked for with the sign-in
 *  token. The service's own refusal is thrown as it wrote it. */
export async function signedFiles(token: string, jobId: string): Promise<SignedFile[]> {
  const r = await fetch(`${FILES_BASE}/${encodeURIComponent(jobId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    // The service says which of several different things went wrong, and
    // repeating it is more use than replacing it with one sentence.
    throw new Error(body.message || `the download service answered ${r.status}`);
  }
  return (body.files ?? []) as SignedFile[];
}

/* Get the file a job produced.
 *
 * The file is written on the engine box, which a browser cannot reach, so it is
 * copied into a private bucket and this asks for a signed link to it. The link
 * is minted at the moment of asking and lasts fifteen minutes, so nothing
 * durable is stored in the page and a link pasted somewhere else stops working.
 *
 * Two steps rather than a plain href, because a plain link cannot carry the
 * sign-in token. The button asks with the token, gets a link back, and follows
 * it. A job that wrote several files shows them, because picking one for
 * somebody would be guessing. */
export function DownloadJobFile({ token, jobId }: { token: string; jobId: string }) {
  const [files, setFiles] = useState<SignedFile[] | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);

  async function ask() {
    setAsking(true);
    setSaid(null);
    try {
      const got = await signedFiles(token, jobId);
      if (got.length === 1 && got[0]) {
        // One file goes straight to the browser's downloads. The link carries
        // its own filename, so this does not navigate away from the page.
        window.location.assign(got[0].url);
        setSaid("Downloading. The link works for fifteen minutes.");
      } else if (got.length > 1) {
        setFiles(got);
      } else {
        setSaid("The job recorded a file and the bucket has none.");
      }
    } catch (e) {
      setSaid(e instanceof Error ? e.message : "the download could not be asked for");
    } finally {
      setAsking(false);
    }
  }

  return (
    <>
      <button onClick={ask} disabled={asking}>{asking ? "Asking" : "Download"}</button>
      {files ? (
        <ul style={{ margin: "0.3rem 0 0", paddingLeft: "1.1rem" }}>
          {files.map((f) => (
            <li key={f.name}>
              <a href={f.url}>{f.name}</a>{" "}
              <span className="muted">{Math.round(f.bytes / 1024).toLocaleString()} KB</span>
            </li>
          ))}
        </ul>
      ) : null}
      {said ? <p className={said.startsWith("Downloading") ? "muted" : "refusal"} role="status">{said}</p> : null}
    </>
  );
}
